/**
 * This tab's id for sidebar changes it has already applied to its own state (a move, a folder create,
 * rename or delete, a resource rename or archive). Those requests carry it in REALTIME_ORIGIN_HEADER, the
 * `resources_updated` event they cause carries it back as `origin`, and the tab skips that event
 * instead of refetching what it already shows.
 *
 * Skipping is only safe while nothing read from the server can predate one of those changes, so this
 * module also counts them per hub: a sidebar read that overlapped one is followed by a catch-up refresh
 * once they have all finished (see `readOverlappedOwnWrite`).
 */

import { uuidv4 } from 'helpers/utils'

export const REALTIME_ORIGIN_HEADER = 'X-Morta-Origin'

let origin: string | undefined
/** This tab's id, made on first use rather than at import, so importing this module has no side effects. */
export const realtimeOrigin = (): string => {
  origin ??= uuidv4()
  return origin
}

/** Fresh request headers naming this tab (the request code adds to whatever it is given). */
export const originHeaders = (): Headers => new Headers({ [REALTIME_ORIGIN_HEADER]: realtimeOrigin() })

/** Own writes in flight, and how many have started, per hub. */
const inFlight = new Map<string, number>()
const started = new Map<string, number>()
/** What to run once a hub has no own write in flight. */
const waiting = new Map<string, () => void>()

/**
 * Sends one of this tab's own sidebar writes for `hubId`: `send` gets the origin headers to attach.
 * Settles exactly as `send`'s promise does.
 */
export const sendOwnWrite = <T>(hubId: string, send: (headers: Headers) => Promise<T>): Promise<T> => {
  inFlight.set(hubId, (inFlight.get(hubId) ?? 0) + 1)
  started.set(hubId, (started.get(hubId) ?? 0) + 1)
  return send(originHeaders()).finally(() => {
    const left = (inFlight.get(hubId) ?? 1) - 1
    if (left > 0) {
      inFlight.set(hubId, left)
      return
    }
    inFlight.delete(hubId)
    const run = waiting.get(hubId)
    waiting.delete(hubId)
    run?.()
  })
}

/** Where own writes stand as a sidebar read for `hubId` is sent; pass it to `readOverlappedOwnWrite`. */
export const ownWriteMark = (hubId: string): number | null => (inFlight.has(hubId) ? null : (started.get(hubId) ?? 0))

/**
 * Whether a sidebar read sent at `mark` may predate one of this tab's own writes: one was in flight when
 * it was sent, or one started since. Its own event may then already have been skipped, so the read
 * can't be trusted to include it.
 */
export const readOverlappedOwnWrite = (hubId: string, mark: number | null): boolean =>
  mark === null || (started.get(hubId) ?? 0) !== mark

/** Runs `run` once no own write for `hubId` is in flight: now if none is, else when the last settles. */
export const afterOwnWrites = (hubId: string, run: () => void): void => {
  if (inFlight.has(hubId)) waiting.set(hubId, run)
  else run()
}
